/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseAuctionPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/auction 入札管理（spec §F）— 開標/締切 + 入札者審核 +
 *                下見預約 + 入札 + ranking + 落札確定 的單一工作區（Tabs）。
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端：v13_bid_collaboration，全部 keyed 到 v13 market_cases 的 case_id。
 *  逐動作 RBAC：
 *   - 開標/締切 = marketplace.bid_collab.round.manage
 *   - 入札者登録 = ...bidder.manage（GET 列表 = ...bid.view）
 *   - 下見枠作成 = ...preview.manage；下見予約 = ...preview.book
 *   - 入札提交 = ...bid.submit（締切後後端拒絕）
 *   - ranking = ...bid.view
 *   - 落札確定 = ...award.confirm（GET 列表 = ...award.view）
 *  単価・金額一律字串型 Decimal，禁 float（CLAUDE.md §2）。
 */

import { useMemo, useState, type ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase, type CaseItemDto } from '../api/cases'
import {
  bookPreviewSlot,
  closeAuctionRound,
  confirmAward,
  createPreviewSlot,
  getRanking,
  listAwards,
  listBidders,
  listPreviewSlots,
  openAuctionRound,
  registerBidder,
  submitBid,
  type BidEntryDto,
  type BiddingRoundDto,
} from '../api/auction'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { useStepUp } from '../components/StepUpDialog'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** datetime-local 字串 → RFC3339（帶本地時區 offset），供後端 FixedOffset 解析。 */
function localInputToRfc3339(value: string): string {
  // new Date(<local>) 以本地時區解讀；toISOString() 會轉 UTC（仍是合法 RFC3339）。
  const d = new Date(value)
  return d.toISOString()
}

/** 顯示時刻：RFC3339 → 本地可讀字串。 */
function fmtTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('ja-JP')
}

/** 千分位（顯示用，不改原始 Decimal 字串）。 */
function fmtAmount(value: string): string {
  const num = Number(value)
  return Number.isFinite(num) ? num.toLocaleString('ja-JP', { maximumFractionDigits: 2 }) : value
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

export function CaseAuctionPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()

  const canRound = can(PERMISSIONS.auctionRoundManage)
  const canBidderManage = can(PERMISSIONS.auctionBidderManage)
  const canPreviewManage = can(PERMISSIONS.auctionPreviewManage)
  const canPreviewBook = can(PERMISSIONS.auctionPreviewBook)
  const canBidSubmit = can(PERMISSIONS.auctionBidSubmit)
  const canBidView = can(PERMISSIONS.auctionBidView)
  const canAwardConfirm = can(PERMISSIONS.auctionAwardConfirm)
  const canAwardView = can(PERMISSIONS.auctionAwardView)

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })
  const biddersQuery = useQuery({
    queryKey: ['case', caseId, 'bidders'],
    queryFn: () => listBidders(caseId),
    enabled: caseId !== '' && canBidView,
  })
  const slotsQuery = useQuery({
    queryKey: ['case', caseId, 'preview-slots'],
    queryFn: () => listPreviewSlots(caseId),
    enabled: caseId !== '' && canBidView,
  })
  const awardsQuery = useQuery({
    queryKey: ['case', caseId, 'awards'],
    queryFn: () => listAwards(caseId),
    enabled: caseId !== '' && canAwardView,
  })

  const items = useMemo<CaseItemDto[]>(() => caseQuery.data?.items ?? [], [caseQuery.data])

  // ---- 開標 / 締切 --------------------------------------------------------
  const [opensAt, setOpensAt] = useState('')
  const [cutoffAt, setCutoffAt] = useState('')
  // 目前窓口：開標/締切回傳的最新 round（後端無 list-round endpoint，故以 mutation 結果為準）。
  const [currentRound, setCurrentRound] = useState<BiddingRoundDto | null>(null)
  const openMutation = useMutation({
    mutationFn: () =>
      openAuctionRound(caseId, {
        opens_at: localInputToRfc3339(opensAt),
        cutoff_at: localInputToRfc3339(cutoffAt),
      }),
    onSuccess: (round) => {
      setCurrentRound(round)
      void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
      toast.success(t('auction.round.openSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.round.openError')}${errDetail(e)}`),
  })
  const closeMutation = useMutation({
    mutationFn: () => closeAuctionRound(caseId),
    onSuccess: (round) => {
      setCurrentRound(round)
      void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
      toast.success(t('auction.round.closeSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.round.closeError')}${errDetail(e)}`),
  })

  // ---- 入札者登録 ---------------------------------------------------------
  const [bidderOrgId, setBidderOrgId] = useState('')
  const registerMutation = useMutation({
    mutationFn: () => registerBidder(caseId, { bidder_org_id: Number(bidderOrgId) }),
    onSuccess: () => {
      setBidderOrgId('')
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'bidders'] })
      toast.success(t('auction.bidders.registerSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.bidders.registerError')}${errDetail(e)}`),
  })

  // ---- 下見枠 / 予約 ------------------------------------------------------
  const [slotStart, setSlotStart] = useState('')
  const [slotEnd, setSlotEnd] = useState('')
  const [slotCapacity, setSlotCapacity] = useState('1')
  const createSlotMutation = useMutation({
    mutationFn: () =>
      createPreviewSlot(caseId, {
        slot_start_at: localInputToRfc3339(slotStart),
        slot_end_at: localInputToRfc3339(slotEnd),
        capacity: Number(slotCapacity) || 1,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'preview-slots'] })
      toast.success(t('auction.preview.createSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.preview.createError')}${errDetail(e)}`),
  })
  const bookMutation = useMutation({
    mutationFn: (slotId: number) => bookPreviewSlot(slotId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'preview-slots'] })
      toast.success(t('auction.preview.bookSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.preview.bookError')}${errDetail(e)}`),
  })

  // ---- 入札提交 -----------------------------------------------------------
  const [bidItemId, setBidItemId] = useState('')
  const [bidPrice, setBidPrice] = useState('')
  const bidMutation = useMutation({
    mutationFn: () =>
      submitBid(caseId, { item_id: Number(bidItemId), unit_price: bidPrice.trim() }),
    onSuccess: () => {
      setBidPrice('')
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'ranking'] })
      toast.success(t('auction.bids.submitSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.bids.submitError')}${errDetail(e)}`),
  })

  // ---- ranking ------------------------------------------------------------
  const [rankItemId, setRankItemId] = useState('')
  const rankingQuery = useQuery({
    queryKey: ['case', caseId, 'ranking', rankItemId],
    queryFn: () => getRanking(caseId, rankItemId),
    enabled: caseId !== '' && rankItemId !== '' && canBidView,
  })

  // ---- 落札確定 -----------------------------------------------------------
  const [awardItemId, setAwardItemId] = useState('')
  const awardRankingQuery = useQuery({
    queryKey: ['case', caseId, 'ranking', awardItemId],
    queryFn: () => getRanking(caseId, awardItemId),
    enabled: caseId !== '' && awardItemId !== '' && canBidView,
  })
  const [winningBidId, setWinningBidId] = useState('')
  // 落札確定 = 高風險端點 → 送出前先 2FA step-up（spec §9.2）。
  const awardStepUp = useStepUp(t)
  const awardMutation = useMutation({
    mutationFn: (code: string) =>
      confirmAward(
        caseId,
        {
          item_id: Number(awardItemId),
          winning_bid_id: Number(winningBidId),
        },
        code,
      ),
    onSuccess: () => {
      setWinningBidId('')
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'awards'] })
      toast.success(t('auction.awards.confirmSuccess'))
    },
    onError: (e) => toast.error(`${t('auction.awards.confirmError')}${errDetail(e)}`),
  })

  const itemLabel = useMemo(() => {
    const map = new Map<number, string>()
    for (const item of items) map.set(item.id, `#${item.line_no} ${item.item_name}`)
    return (id: number): string => map.get(id) ?? `#${id}`
  }, [items])

  if (caseQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.detail.loadError')}
        {errDetail(caseQuery.error)}
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.auction')}</span>
        <h2 className="text-lg font-semibold">{t('auction.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('auction.subtitle')}</p>
      </header>

      <Tabs defaultValue="round">
        <TabsList className="flex-wrap">
          <TabsTrigger value="round">{t('auction.tab.round')}</TabsTrigger>
          <TabsTrigger value="bidders">{t('auction.tab.bidders')}</TabsTrigger>
          <TabsTrigger value="preview">{t('auction.tab.preview')}</TabsTrigger>
          <TabsTrigger value="bids">{t('auction.tab.bids')}</TabsTrigger>
          <TabsTrigger value="ranking">{t('auction.tab.ranking')}</TabsTrigger>
          <TabsTrigger value="awards">{t('auction.tab.awards')}</TabsTrigger>
        </TabsList>

        {/* 1. 開標 / 締切 */}
        <TabsContent value="round">
          <Card>
            <CardHeader>
              <CardTitle>{t('auction.round.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {currentRound ? (
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                  <Stat label={t('auction.round.opensAt')} value={fmtTime(currentRound.opens_at)} />
                  <Stat label={t('auction.round.cutoffAt')} value={fmtTime(currentRound.cutoff_at)} />
                  <Stat label={t('auction.round.status')} value={currentRound.status} />
                  <Stat label={t('auction.round.closedAt')} value={fmtTime(currentRound.closed_at)} />
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">{t('auction.round.none')}</p>
              )}
              {canRound ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.round.opensAt')}</span>
                    <Input
                      type="datetime-local"
                      value={opensAt}
                      onChange={(e) => setOpensAt(e.target.value)}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.round.cutoffAt')}</span>
                    <Input
                      type="datetime-local"
                      value={cutoffAt}
                      onChange={(e) => setCutoffAt(e.target.value)}
                    />
                  </label>
                  <div className="flex items-end gap-2">
                    <Button
                      data-testid="auction-open-round"
                      onClick={() => openMutation.mutate()}
                      disabled={openMutation.isPending || !opensAt || !cutoffAt}
                    >
                      {openMutation.isPending ? t('auction.round.opening') : t('auction.round.open')}
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="auction-close-round"
                      onClick={() => closeMutation.mutate()}
                      disabled={closeMutation.isPending}
                    >
                      {closeMutation.isPending
                        ? t('auction.round.closing')
                        : t('auction.round.close')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Badge variant="outline">{t('rbac.forbidden')}</Badge>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. 入札者登録 + 審核 */}
        <TabsContent value="bidders">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('auction.bidders.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t('auction.bidders.hint')}</p>
              {canBidderManage ? (
                <form
                  className="flex max-w-md gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (bidderOrgId.trim()) registerMutation.mutate()
                  }}
                >
                  <Input
                    inputMode="numeric"
                    value={bidderOrgId}
                    data-testid="bidder-org-id"
                    placeholder={t('auction.bidders.orgPlaceholder')}
                    aria-label={t('auction.bidders.orgId')}
                    onChange={(e) => setBidderOrgId(e.target.value)}
                  />
                  <Button
                    type="submit"
                    data-testid="bidder-register"
                    disabled={registerMutation.isPending || !bidderOrgId.trim()}
                  >
                    {registerMutation.isPending
                      ? t('auction.bidders.registering')
                      : t('auction.bidders.register')}
                  </Button>
                </form>
              ) : null}
              <BidderTable t={t} canView={canBidView} query={biddersQuery} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. 下見預約 */}
        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle>{t('auction.preview.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t('auction.preview.capacityHint')}</p>
              {canPreviewManage ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.preview.startAt')}</span>
                    <Input
                      type="datetime-local"
                      value={slotStart}
                      onChange={(e) => setSlotStart(e.target.value)}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.preview.endAt')}</span>
                    <Input
                      type="datetime-local"
                      value={slotEnd}
                      onChange={(e) => setSlotEnd(e.target.value)}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.preview.capacity')}</span>
                    <Input
                      inputMode="numeric"
                      value={slotCapacity}
                      onChange={(e) => setSlotCapacity(e.target.value)}
                    />
                  </label>
                  <div className="flex items-end">
                    <Button
                      data-testid="preview-slot-create"
                      onClick={() => createSlotMutation.mutate()}
                      disabled={createSlotMutation.isPending || !slotStart || !slotEnd}
                    >
                      {createSlotMutation.isPending
                        ? t('auction.preview.creating')
                        : t('auction.preview.create')}
                    </Button>
                  </div>
                </div>
              ) : null}
              <PreviewSlotTable
                t={t}
                canView={canBidView}
                canBook={canPreviewBook}
                booking={bookMutation.isPending}
                onBook={(slotId) => bookMutation.mutate(slotId)}
                query={slotsQuery}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4. 入札提交 */}
        <TabsContent value="bids">
          <Card>
            <CardHeader>
              <CardTitle>{t('auction.bids.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t('auction.bids.closedHint')}</p>
              {canBidSubmit ? (
                <form
                  className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (bidItemId && bidPrice.trim()) bidMutation.mutate()
                  }}
                >
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.bids.item')}</span>
                    <ItemSelect
                      items={items}
                      value={bidItemId}
                      placeholder={t('auction.bids.selectItem')}
                      onChange={setBidItemId}
                      testIdPrefix="bid-item"
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.bids.unitPrice')}</span>
                    <Input
                      inputMode="decimal"
                      value={bidPrice}
                      data-testid="bid-unit-price"
                      placeholder={t('auction.bids.unitPricePlaceholder')}
                      onChange={(e) => setBidPrice(e.target.value)}
                    />
                  </label>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      data-testid="bid-submit"
                      disabled={bidMutation.isPending || !bidItemId || !bidPrice.trim()}
                    >
                      {bidMutation.isPending
                        ? t('auction.bids.submitting')
                        : t('auction.bids.submit')}
                    </Button>
                  </div>
                </form>
              ) : (
                <Badge variant="outline">{t('rbac.forbidden')}</Badge>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. Ranking */}
        <TabsContent value="ranking">
          <Card>
            <CardHeader>
              <CardTitle>{t('auction.ranking.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t('auction.ranking.tieHint')}</p>
              <label className="block max-w-xs space-y-1 text-sm">
                <span className="text-muted-foreground">{t('auction.ranking.selectItem')}</span>
                <ItemSelect
                  items={items}
                  value={rankItemId}
                  placeholder={t('auction.ranking.selectItem')}
                  onChange={setRankItemId}
                />
              </label>
              <RankingTable t={t} canView={canBidView} selected={rankItemId} query={rankingQuery} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* 6. 落札確定 */}
        <TabsContent value="awards">
          <Card>
            <CardHeader>
              <CardTitle>{t('auction.awards.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t('auction.awards.feedsP2')}</p>
              {canAwardConfirm ? (
                <form
                  className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (awardItemId && winningBidId) {
                      awardStepUp.request((code) => awardMutation.mutate(code))
                    }
                  }}
                >
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.awards.item')}</span>
                    <ItemSelect
                      items={items}
                      value={awardItemId}
                      placeholder={t('auction.awards.item')}
                      testIdPrefix="award-item"
                      onChange={(v) => {
                        setAwardItemId(v)
                        setWinningBidId('')
                      }}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="text-muted-foreground">{t('auction.awards.winningBid')}</span>
                    <Select value={winningBidId} onValueChange={setWinningBidId}>
                      <SelectTrigger data-testid="award-winning-bid-trigger">
                        <SelectValue placeholder={t('auction.awards.winningBid')} />
                      </SelectTrigger>
                      <SelectContent>
                        {(awardRankingQuery.data ?? []).map((bid) => (
                          <SelectItem
                            key={bid.id}
                            value={String(bid.id)}
                            data-testid={`award-winning-bid-option-${bid.id}`}
                          >
                            #{bid.id} · org {bid.bidder_org_id} · ¥{fmtAmount(bid.unit_price)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      data-testid="award-confirm"
                      disabled={awardMutation.isPending || !awardItemId || !winningBidId}
                    >
                      {awardMutation.isPending
                        ? t('auction.awards.confirming')
                        : t('auction.awards.confirm')}
                    </Button>
                  </div>
                  {awardStepUp.dialog}
                </form>
              ) : (
                <Badge variant="outline">{t('rbac.forbidden')}</Badge>
              )}
              <AwardTable
                t={t}
                canView={canAwardView}
                itemLabel={itemLabel}
                query={awardsQuery}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---- 小元件 ---------------------------------------------------------------

function Stat({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="rounded-md border border-border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  )
}

/** 案件品項下拉（shadcn Select），value 為品項 id 字串。 */
function ItemSelect({
  items,
  value,
  placeholder,
  onChange,
  testIdPrefix,
}: {
  items: CaseItemDto[]
  value: string
  placeholder: string
  onChange: (value: string) => void
  /** 可選：給 E2E 用的穩定 testid 前綴（trigger=`${prefix}-trigger`，option=`${prefix}-option-<itemId>`）。 */
  testIdPrefix?: string
}): ReactElement {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testIdPrefix ? `${testIdPrefix}-trigger` : undefined}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem
            key={item.id}
            value={String(item.id)}
            data-testid={testIdPrefix ? `${testIdPrefix}-option-${item.id}` : undefined}
          >
            #{item.line_no} {item.item_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface QueryLike<T> {
  data?: T
  isPending: boolean
  isError: boolean
}

function BidderTable({
  t,
  canView,
  query,
}: {
  t: (key: string) => string
  canView: boolean
  query: QueryLike<import('../api/auction').BidderDto[]>
}): ReactElement {
  if (!canView) return <Badge variant="outline">{t('rbac.forbidden')}</Badge>
  if (query.isPending) return <Skeleton className="h-24 w-full" />
  const rows = query.data ?? []
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{t('auction.bidders.empty')}</p>
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>{t('auction.bidders.orgId')}</TableHead>
            <TableHead>{t('auction.bidders.invitedAt')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((b) => (
            <TableRow key={b.id}>
              <TableCell>#{b.id}</TableCell>
              <TableCell>{b.bidder_org_id}</TableCell>
              <TableCell>{fmtTime(b.invited_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function PreviewSlotTable({
  t,
  canView,
  canBook,
  booking,
  onBook,
  query,
}: {
  t: (key: string) => string
  canView: boolean
  canBook: boolean
  booking: boolean
  onBook: (slotId: number) => void
  query: QueryLike<import('../api/auction').PreviewSlotDto[]>
}): ReactElement {
  if (!canView) return <Badge variant="outline">{t('rbac.forbidden')}</Badge>
  if (query.isPending) return <Skeleton className="h-24 w-full" />
  const rows = query.data ?? []
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{t('auction.preview.empty')}</p>
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>{t('auction.preview.startAt')}</TableHead>
            <TableHead>{t('auction.preview.endAt')}</TableHead>
            <TableHead>{t('auction.preview.capacity')}</TableHead>
            <TableHead>{t('auction.preview.status')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.id}>
              <TableCell>#{s.id}</TableCell>
              <TableCell>{fmtTime(s.slot_start_at)}</TableCell>
              <TableCell>{fmtTime(s.slot_end_at)}</TableCell>
              <TableCell>{s.capacity}</TableCell>
              <TableCell>{s.status}</TableCell>
              <TableCell className="text-right">
                {canBook ? (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`preview-book-${s.id}`}
                    disabled={booking}
                    onClick={() => onBook(s.id)}
                  >
                    {booking ? t('auction.preview.booking') : t('auction.preview.book')}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function RankingTable({
  t,
  canView,
  selected,
  query,
}: {
  t: (key: string) => string
  canView: boolean
  selected: string
  query: QueryLike<BidEntryDto[]>
}): ReactElement {
  if (!canView) return <Badge variant="outline">{t('rbac.forbidden')}</Badge>
  if (selected === '')
    return <p className="text-sm text-muted-foreground">{t('auction.ranking.selectItem')}</p>
  if (query.isPending) return <Skeleton className="h-24 w-full" />
  const rows = query.data ?? []
  if (rows.length === 0)
    return <p className="text-sm text-muted-foreground">{t('auction.ranking.empty')}</p>
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('auction.ranking.rank')}</TableHead>
            <TableHead>{t('auction.ranking.bidder')}</TableHead>
            <TableHead className="text-right">{t('auction.ranking.unitPrice')}</TableHead>
            <TableHead>{t('auction.ranking.submittedAt')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((bid, index) => (
            <TableRow key={bid.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>org {bid.bidder_org_id}</TableCell>
              <TableCell className="text-right">¥{fmtAmount(bid.unit_price)}</TableCell>
              <TableCell>{fmtTime(bid.submitted_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function AwardTable({
  t,
  canView,
  itemLabel,
  query,
}: {
  t: (key: string) => string
  canView: boolean
  itemLabel: (id: number) => string
  query: QueryLike<import('../api/auction').AwardDto[]>
}): ReactElement {
  if (!canView) return <Badge variant="outline">{t('rbac.forbidden')}</Badge>
  if (query.isPending) return <Skeleton className="h-24 w-full" />
  const rows = query.data ?? []
  if (rows.length === 0)
    return <p className="text-sm text-muted-foreground">{t('auction.awards.empty')}</p>
  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('auction.awards.item')}</TableHead>
            <TableHead>{t('auction.awards.awardee')}</TableHead>
            <TableHead className="text-right">{t('auction.awards.unitPrice')}</TableHead>
            <TableHead>{t('auction.awards.confirmedAt')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id}>
              <TableCell>{itemLabel(a.item_id)}</TableCell>
              <TableCell>org {a.awarded_bidder_org_id}</TableCell>
              <TableCell className="text-right">¥{fmtAmount(a.auction_unit_price)}</TableCell>
              <TableCell>{fmtTime(a.confirmed_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
